import { useState, useCallback, useMemo, useRef } from "react";
import { useHourClips, useDayClipCounts } from "./useDayClips";
import type { Clip } from "../types/clip";
import type { HourBucket } from "./useDayClips";
import { downloadVideo, handleDownloadError } from "../utils";
import { getHours, startOfDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useAppState } from "@src/hooks/useAppState";
import { trackEvent } from "@src/services/analytics/amplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { useRecordActivity } from "@visioni/a3s-activity/app";

// ============================================================================
// Types
// ============================================================================

type InitialSelection = {
  date?: Date;
  hour?: number;
  clipId?: string;
};

type UseClipsLogicParams = {
  siteId: string;
  cameraKey: string;
  timezone: string;
  initialSelection?: InitialSelection;
  enabled?: boolean;
};

type UseClipsLogicReturn = {
  // Date/hour selection
  selectedDate: Date;
  selectedHour: number;
  /** "Today" in the camera's timezone — use for calendar boundary checks */
  today: Date;
  dateDirection: "forward" | "backward";
  handleDateSelect: (date: Date) => void;
  handleHourSelect: (hour: number) => void;

  // Hourly counts (for heatmap)
  hourBuckets: HourBucket[];
  isLoadingHourlyClipCounts: boolean;

  // Clips for the selected hour
  clipsData: Clip[] | undefined;
  isLoadingClips: boolean;
  isFetchingClips: boolean;
  clipsError: Error | null;
  refetchAll: () => void;

  // Clip selection & navigation
  selectedClip: Clip | null;
  selectedClipId: string | null;
  selectedClipIndex: number;
  totalClipsInHour: number;
  handleClipSelect: (clipId: string) => void;
  handlePrevious: () => void;
  handleNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  handleClipViewed: (clip: Clip) => void;

  // Pagination
  hasMore: boolean;
  handleLoadMore: () => void;

  // Download
  isDownloading: boolean;
  handleDownload: () => Promise<void>;
};

// ============================================================================
// Hook
// ============================================================================

export function useClipsLogic({
  siteId,
  cameraKey,
  timezone,
  initialSelection,
  enabled = true,
}: UseClipsLogicParams): UseClipsLogicReturn {
  const appState = useAppState();

  // "now" in the camera's timezone — used for default date/hour.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- appState is intentional: cache-bust on foreground
  const zonedNow = useMemo(() => toZonedTime(new Date(), timezone), [timezone, appState]);
  const today = useMemo(() => startOfDay(zonedNow), [zonedNow]);

  const [selectedDate, setSelectedDate] = useState<Date>(() => initialSelection?.date ?? startOfDay(zonedNow));
  const [selectedHour, setSelectedHour] = useState<number | null>(initialSelection?.hour ?? null);
  const [selectedClipId, setSelectedClipId] = useState<string | null>(initialSelection?.clipId ?? null);
  const [isDownloading, setIsDownloading] = useState(false);
  const dateDirectionRef = useRef<"forward" | "backward">("forward");
  const mediaViewPathParams = useMemo(() => {
    if (!siteId || !cameraKey) return undefined;
    return {
      siteId: SiteId.fromString(siteId),
      assetCameraKey: AssetCameraKey.fromString(cameraKey),
    };
  }, [siteId, cameraKey]);
  const { mutate: recordActivity } = useRecordActivity(mediaViewPathParams);

  // When the timezone changes, reset the selected date/hour/clip
  const [prevTimezone, setPrevTimezone] = useState(timezone);
  if (timezone !== prevTimezone) {
    setPrevTimezone(timezone);
    setSelectedDate(initialSelection?.date ?? startOfDay(zonedNow));
    setSelectedHour(initialSelection?.hour ?? null);
    setSelectedClipId(initialSelection?.clipId ?? null);
  }

  const {
    isLoading: isLoadingHourlyClipCounts,
    hourBuckets,
    refetch: refetchHourlyCounts,
  } = useDayClipCounts({ siteId, cameraKey, selectedDate, timezone, enabled });

  // Derive the latest hour that has clips — used as default when user hasn't explicitly picked one
  const latestHourWithClips = useMemo(() => {
    for (let i = hourBuckets.length - 1; i >= 0; i -= 1) {
      if (hourBuckets[i]?.count) {
        return hourBuckets[i].hour;
      }
    }
    return null;
  }, [hourBuckets]);

  const effectiveHour = selectedHour ?? latestHourWithClips ?? getHours(zonedNow);

  const {
    clips: hourClips,
    loadMore,
    refetch: refetchHourClips,
    hasMore,
    isLoading: isLoadingClips,
    isFetching: isFetchingClips,
    error,
  } = useHourClips({ siteId, cameraKey, selectedDate, selectedHour: effectiveHour, timezone, enabled });

  // ---- Derived data ----

  const selectedClip = useMemo(() => {
    if (!hourClips.length) return null;
    const firstClip = hourClips[0];
    if (selectedClipId === null) return firstClip;
    return hourClips.find((c) => c.id === selectedClipId) ?? firstClip;
  }, [selectedClipId, hourClips]);

  const selectedClipIndex = useMemo(() => {
    if (!selectedClip) return 0;
    const index = hourClips.findIndex((c) => c.id === selectedClip.id);
    return index >= 0 ? index : 0;
  }, [selectedClip, hourClips]);

  const totalClipsInHour = hourBuckets[effectiveHour]?.count ?? 0;

  // ---- Handlers ----

  const handleDateSelect = useCallback(
    (date: Date) => {
      dateDirectionRef.current = date > selectedDate ? "forward" : "backward";
      setSelectedDate(date);
      setSelectedHour(null);
      setSelectedClipId(null);
    },
    [selectedDate],
  );

  const handleHourSelect = useCallback((hour: number) => {
    setSelectedHour(hour);
    setSelectedClipId(null);
  }, []);

  const handleClipSelect = useCallback((clipId: string) => {
    setSelectedClipId(clipId);
  }, []);

  const handlePrevious = useCallback(() => {
    if (hourClips.length && selectedClipIndex > 0) {
      setSelectedClipId(hourClips[selectedClipIndex - 1].id);
    }
  }, [hourClips, selectedClipIndex]);

  const handleNext = useCallback(() => {
    if (hourClips.length && selectedClipIndex < hourClips.length - 1) {
      setSelectedClipId(hourClips[selectedClipIndex + 1].id);
    }
  }, [hourClips, selectedClipIndex]);

  const handleClipViewed = useCallback(
    (clip: Clip) => {
      if (!mediaViewPathParams) return;
      recordActivity(
        { kind: "clip_viewed", clipId: clip.id },
        {
          onError: (error) => {
            console.warn("Failed to record clip view activity:", error);
          },
        },
      );
    },
    [mediaViewPathParams, recordActivity],
  );

  const handleLoadMore = useCallback(() => {
    if (hasMore) {
      loadMore();
    }
  }, [hasMore, loadMore]);

  const handleDownload = useCallback(async () => {
    if (!selectedClip?.videoUrl) return;
    trackEvent(ANALYTICS_EVENTS.CLIP_DOWNLOAD, {
      clip_id: selectedClip.id,
      camera_key: cameraKey,
    });
    try {
      setIsDownloading(true);
      await downloadVideo({
        videoUrl: selectedClip.videoUrl,
        timestamp: new Date(selectedClip.timestamp),
      });
    } catch (err: unknown) {
      handleDownloadError(err);
    } finally {
      setIsDownloading(false);
    }
  }, [selectedClip, cameraKey]);

  const refetchAll = useCallback(() => {
    refetchHourlyCounts();
    refetchHourClips();
  }, [refetchHourlyCounts, refetchHourClips]);

  return {
    selectedDate,
    selectedHour: effectiveHour,
    today,
    dateDirection: dateDirectionRef.current,
    handleDateSelect,
    handleHourSelect,

    hourBuckets,
    isLoadingHourlyClipCounts,

    clipsData: isLoadingClips ? undefined : hourClips,
    isLoadingClips,
    isFetchingClips,
    clipsError: error instanceof Error ? error : null,
    refetchAll,

    selectedClip,
    selectedClipId,
    selectedClipIndex,
    totalClipsInHour,
    handleClipSelect,
    handlePrevious,
    handleNext,
    hasPrevious: selectedClipIndex > 0,
    hasNext: selectedClipIndex < hourClips.length - 1,
    handleClipViewed,

    hasMore,
    handleLoadMore,

    isDownloading,
    handleDownload,
  };
}
